/**
 * Rules for a position that may be trusted (F12, F14). The phone, the server and the tests all use these, so they cannot drift.
 * A web page cannot see whether a fake-location app is installed; these rules refuse old, vague and impossible positions, and positions outside the city.
 * The Android app will add the phone's own "mock location" flag (docs/QA.md, debt 2).
 */
import { haversineKm, type LatLng } from './geo';
import { MAP_CITIES, type CityBox } from './maps';

/** A position older than this is not used to go online or post a request. */
export const GPS_FRESH_MS = 30_000;
/** A position vaguer than this (the phone's own accuracy estimate, in metres) is not used. */
export const GPS_MAX_ACCURACY_M = 100;
/** Faster than this between two positions is not possible for a car, so the new position is refused. */
export const MAX_PLAUSIBLE_KMH = 200;
/** Below this distance a jump is just GPS noise, whatever the time between the two positions. */
export const JUMP_IGNORE_KM = 1;
/** A trip can start only when the driver and the rider are this close (the same 250 m the app uses to mark the driver as arrived; GPS in a street is good to about 20 to 50 m). */
export const PICKUP_MAX_M = 250;

export interface Fix extends LatLng { at: number; acc: number }

/** '' when the position may be used; otherwise the message for the person. */
export function fixProblem(fix: Fix | null, now: number): string {
  if (!fix) return 'Waiting for your location. Allow location for this page, then try again.';
  if (Math.abs(now - fix.at) > GPS_FRESH_MS) return 'Your location is out of date. Move to open sky and try again.';
  if (!(fix.acc <= GPS_MAX_ACCURACY_M)) return `Your location is not exact enough yet (about ${Math.round(fix.acc)} m). Go outside and try again.`;
  return '';
}

/** True when getting from a to b in this time would need more than MAX_PLAUSIBLE_KMH. Short moves are never refused. */
export function impossibleJump(a: LatLng, b: LatLng, ms: number): boolean {
  const km = haversineKm(a, b);
  if (km <= JUMP_IGNORE_KM) return false;
  return km / (Math.max(ms, 1000) / 3_600_000) > MAX_PLAUSIBLE_KMH;
}

/** The database writes "port-harcourt", the map files "portharcourt": one city, so ids are compared without dashes and case. */
export const sameCity = (a: string, b: string): boolean => a.replace(/[^a-z]/gi, '').toLowerCase() === b.replace(/[^a-z]/gi, '').toLowerCase();

/** Is the position inside the named city? A city we have no area for cannot be checked, so it counts as inside. */
export function insideCity(cityId: string, p: LatLng, marginDeg = 0.05): boolean {
  const c: CityBox | undefined = MAP_CITIES.find((x) => sameCity(x.id, cityId));
  return !c || (p.lat >= c.latMin - marginDeg && p.lat <= c.latMax + marginDeg && p.lng >= c.lngMin - marginDeg && p.lng <= c.lngMax + marginDeg);
}
export const cityName = (cityId: string): string => MAP_CITIES.find((x) => sameCity(x.id, cityId))?.name ?? cityId;
